import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, EMPTY, Observable, Subject, firstValueFrom, of, tap, takeUntil, timeout } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { authConfig, REQUIRED_ROLE } from './auth.config';
import { UserDetails, OwnAccount } from './user-details.model';
import { PermissionService } from './permission.service';
import { LoggingService } from '../services/logging.service';
import { safeHttpMessage } from '../error/safe-http-message';
import { environment } from '../../../environments/environment';

export type AccessState = 'loading' | 'authorized' | 'denied' | 'denied-error';

/** EB-4580: what detected the expired session (logged with the auth event). */
/** Verdict of the auth server's `check_token`. @ticket EB-4580 */
type TokenCheckResult = 'valid' | 'rejected' | 'unavailable';

export type SessionExpiredTrigger = 'http-401' | 'expired-before-request' | 'tab-visible';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly RETURN_URL_KEY = 'cmt-auth-return-url';
  // EB-3781: counter for failed implicit-flow fragment processing attempts.
  // EB-3819: stored in localStorage (not sessionStorage) so a lost tab session
  // — the same failure mode that wipes the nonce and triggers the retry —
  // can't also reset the counter to 0 and turn the bounded retry into an
  // unbounded redirect loop. Cleared once login succeeds / on logout.
  private static readonly IMPLICIT_RETRY_KEY = 'cmt-auth-implicit-retry';
  private static readonly IMPLICIT_RETRY_MAX = 2;
  // EB-3781 / Code-Review M3+I1: shared timeout for auth-server round-trips.
  // Both /oauth/check_token and /userdetails hit the same host with similar
  // payload size; using one constant keeps them in sync.
  private static readonly AUTH_TIMEOUT_MS = 15_000;
  // EB-4580: timestamp of the last runtime re-auth (see handleSessionExpired).
  private static readonly SESSION_EXPIRED_REAUTH_AT_KEY = 'cmt-auth-session-expired-reauth-at';
  private static readonly SESSION_EXPIRED_REAUTH_COOLDOWN_MS = 60_000;
  /** How long a `valid` check_token verdict after a 401 is reused (EB-4580). */
  private static readonly TOKEN_VALID_VERDICT_TTL_MS = 60_000;

  private oauthService = inject(OAuthService);
  private http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);
  // EB-3798: LoggingService resolves AuthService/SessionService lazily via the
  // Injector, so injecting it here does not create a circular dependency.
  private readonly loggingService = inject(LoggingService);

  private userDetails$ = new BehaviorSubject<UserDetails | null>(null);
  readonly user$ = this.userDetails$.asObservable();

  // EB-3801: logged-in employee's own customer/UA/PS — populated lazily once
  // via /oauth/check_token (carries `details.userAccountId` + `phoneServiceData`)
  // plus /useraccount/v1/useraccounts/{id} for the parent customerId.
  private readonly _ownAccount = signal<OwnAccount | null>(null);
  readonly ownAccount = this._ownAccount.asReadonly();
  // M9: signal-based so templates could bind a spinner while the fetch is in
  // flight. Currently only read internally as an idempotency guard.
  private readonly ownAccountLoading = signal<boolean>(false);
  // H2: emits on logout() so any in-flight ensureOwnAccountLoaded() chain is
  // cancelled before it can race-write a stale ownAccount value.
  private readonly logoutTrigger$ = new Subject<void>();

  // EB-3775: role-gate state drives the root shell @switch in app.html.
  private readonly _accessState = signal<AccessState>('loading');
  readonly accessState = this._accessState.asReadonly();

  // EB-4580: one re-auth redirect per page life — parallel 401s must not each start one.
  private sessionExpiredRedirecting = false;
  private unauthorizedCheckInFlight = false;
  /** When `check_token` last called the token valid after a 401 (0 = never). @ticket EB-4580 */
  private tokenValidVerdictAt = 0;
  private tokenExpiryWatchStarted = false;
  // EB-4580: set for the whole /ssologout round-trip; a 401 racing the logout must not re-auth.
  private loggingOut = false;

  async configure(): Promise<void> {
    this.oauthService.configure(authConfig);
    // EB-3819: tryLoginImplicitFlow REJECTS (not resolve-false) when the
    // returned state's nonce doesn't match the stored one — see
    // angular-oauth2-oidc tryLoginImplicitFlow:
    // `return Promise.reject(new OAuthErrorEvent('invalid_nonce_in_state'))`.
    // configure() is the APP_INITIALIZER; an uncaught rejection here aborts
    // Angular bootstrap → blank white screen, AND the fragment-stall recovery
    // below never runs (it sits after this await). Swallow the rejection so
    // hasUnconsumedAccessTokenFragment() can scrub the stale fragment and
    // retry the OAuth round-trip instead of stranding the user on a blank page.
    try {
      await this.oauthService.tryLoginImplicitFlow();
    } catch (err) {
      // OAuthErrorEvent has no toString → String(err) would log "[object
      // Object]". Prefer its `.type` (e.g. 'invalid_nonce_in_state') so the
      // Graylog entry is actually diagnosable.
      const reason = (err as { type?: string } | null)?.type ?? String(err);
      this.logAuthEvent('implicit-flow-rejected', { error: reason });
    }

    // EB-3781 / Code-Review M4: OAuth provider may also redirect back with
    // `#error=access_denied&error_description=…` (user cancelled, server
    // rejected, …). Without surfacing this, we'd land on a blank screen with
    // the error silently lost. Treat as a hard denied-error.
    if (this.hasOAuthErrorFragment()) {
      this.logAuthEvent('oauth-error-fragment', { hash: this.safeHashSnapshot() });
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', '/');
      }
      this._accessState.set('denied-error');
      return;
    }

    // EB-3781: hash-fragment-stall recovery. If `tryLoginImplicitFlow` could
    // not consume an `#access_token=…` fragment (state/nonce mismatch, lost
    // sessionStorage entry, clock-skew on nonce TTL, …) the URL stays at
    // `…/#access_token=…&state=…` and `hasValidAccessToken()` returns false →
    // user is stranded on a blank screen. Detect that, scrub the fragment,
    // and retry the OAuth round-trip. Bounded by IMPLICIT_RETRY_MAX so a
    // genuinely broken auth setup can't redirect-loop indefinitely.
    if (this.hasUnconsumedAccessTokenFragment()) {
      const attempts = Number(localStorage.getItem(AuthService.IMPLICIT_RETRY_KEY) ?? '0');
      if (attempts < AuthService.IMPLICIT_RETRY_MAX) {
        localStorage.setItem(AuthService.IMPLICIT_RETRY_KEY, String(attempts + 1));
        this.logAuthEvent('fragment-stall-retry', { attempt: attempts + 1 });
        if (typeof window !== 'undefined') {
          // Code-Review M1: preserve query string — pathname alone would drop
          // any `?foo=bar` the user deep-linked with.
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          );
        }
        this.oauthService.initImplicitFlow();
        return;
      }
      // 2× retry exhausted → surface a real error instead of a blank screen.
      localStorage.removeItem(AuthService.IMPLICIT_RETRY_KEY);
      this.logAuthEvent('fragment-stall-exhausted', { maxAttempts: AuthService.IMPLICIT_RETRY_MAX });
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', '/');
      }
      this._accessState.set('denied-error');
      return;
    }
    // Successful pass through tryLoginImplicitFlow → reset retry counter.
    localStorage.removeItem(AuthService.IMPLICIT_RETRY_KEY);

    // Deep-link restore: redirectUri is fixed to origin, so the original path
    // (e.g. /admin/brands/1/edit) is dropped on the OAuth round-trip. We stash
    // it in sessionStorage before redirecting (see `login()` + authGuard) —
    // sessionStorage survives same-tab redirects — and rewrite the URL bar
    // before the Angular router bootstraps so it picks up the intended route.
    // Avoids using OAuth `state` since some providers reject URL-encoded
    // slashes in state, which fails silent re-auth.
    const stashed = sessionStorage.getItem(AuthService.RETURN_URL_KEY);
    if (stashed && this.oauthService.hasValidAccessToken()) {
      sessionStorage.removeItem(AuthService.RETURN_URL_KEY);
      // Same-origin guard — only accept absolute in-app paths.
      if (stashed.startsWith('/') && !stashed.startsWith('//')) {
        window.history.replaceState(null, '', stashed);
      }
    }

    // EB-3775: gate access on REQUIRED_ROLE before bootstrapping the shell.
    // NOTE: If `tryLoginImplicitFlow` returned without a token, the OAuth
    // provider has already redirected the browser away — this branch is not
    // reached. If silent re-auth fails (`sessionChecksEnabled=false` in
    // auth.config), `accessState` stays 'loading' and the user sees the
    // spinner; a full page reload triggers OAuth again. Codified by the
    // existing 'no token → evaluateAccess skipped' spec test (CR-7).
    if (this.oauthService.hasValidAccessToken()) {
      // EB-3781: server-side token validation before bootstrapping. The local
      // `hasValidAccessToken()` only checks `expires_at` client-side — a
      // revoked token or clock-skew would fall through to /userdetails and
      // land the user on the access-denied error screen. Mirror the legacy
      // CMT (authenticationResolverService.js:51) and ask the auth server.
      const serverValid = await this.verifyTokenWithServer();
      if (serverValid) {
        await this.evaluateAccess();
      } else {
        this.logAuthEvent('check-token-rejected', {});
        this.reauthenticate();
      }
    }
  }

  /**
   * EB-3781: True when the URL still carries an unconsumed OAuth implicit-flow
   * fragment (`#access_token=…`). Indicates `tryLoginImplicitFlow` did not
   * process it (state/nonce/sessionStorage issue) — without recovery the user
   * sees a blank screen.
   */
  private hasUnconsumedAccessTokenFragment(): boolean {
    if (typeof window === 'undefined') return false;
    if (this.oauthService.hasValidAccessToken()) return false;
    const hash = window.location.hash ?? '';
    return /(^|[#&])access_token=/.test(hash);
  }

  /**
   * Code-Review M4: True when the OAuth provider redirected back with an
   * error fragment (e.g. `#error=access_denied&error_description=…`).
   */
  private hasOAuthErrorFragment(): boolean {
    if (typeof window === 'undefined') return false;
    const hash = window.location.hash ?? '';
    return /(^|[#&])error=/.test(hash);
  }

  /**
   * Code-Review M4: Returns a minimal, non-sensitive view of the current hash
   * for logging — strips any `access_token=…`/`id_token=…` parameter values
   * before they end up in the LoggingService / Graylog. We keep parameter NAMES
   * so the log is useful for diagnosis.
   */
  private safeHashSnapshot(): string {
    if (typeof window === 'undefined') return '';
    return (window.location.hash ?? '').replace(
      /(access_token|id_token|refresh_token)=[^&]*/g,
      '$1=<redacted>',
    );
  }

  /**
   * EB-3781: Calls POST /oauth/check_token to verify the token server-side.
   * Returns false ONLY for definitive rejections — 400/401 OR a 200 response
   * carrying `{ active: false }` (newer Spring Authorization Server). For
   * transient failures (5xx, timeout, network) returns true so we fall back
   * to the existing /userdetails path — better to attempt bootstrap than to
   * lock users out on a server hiccup.
   *
   * `active !== false` (not `=== true`) is deliberate: the ebuero auth server
   * may not include `active` in the body at all, in which case the body shape
   * mirrors the legacy `{ user_name, authorities, … }` payload and we should
   * default to "valid" rather than locking everyone out.
   */
  private async verifyTokenWithServer(): Promise<boolean> {
    return (await this.checkToken()) !== 'rejected';
  }

  /**
   * Tri-state `check_token` verdict: `rejected` (400/401 or `active: false`),
   * `unavailable` (5xx, timeout, network) or `valid`. Lets the 401 handler log an
   * unreachable auth server separately from a backend that misuses 401.
   * @ticket EB-4580
   */
  private checkToken(): Promise<TokenCheckResult> {
    const token = this.oauthService.getAccessToken();
    if (!token) return Promise.resolve('rejected');
    // Code-Review I1: token goes in the form-encoded POST body, NOT the URL
    // query string — keeps it out of browser history, proxy access logs, and
    // HTTP referrer headers (matches RFC 7662 token-introspection convention).
    const url = `${environment.authUrl}/oauth/check_token`;
    const body = new URLSearchParams({ token }).toString();
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' });
    return firstValueFrom(
      this.http
        .post<{ active?: boolean }>(url, body, { headers })
        .pipe(
          timeout(AuthService.AUTH_TIMEOUT_MS),
          map((body): TokenCheckResult => (body?.active === false ? 'rejected' : 'valid')),
          catchError((err: unknown) => {
            const status = err instanceof HttpErrorResponse ? err.status : 0;
            return of<TokenCheckResult>(status === 400 || status === 401 ? 'rejected' : 'unavailable');
          }),
        ),
    );
  }

  /**
   * EB-3781: Token rejected server-side → clear local state and bounce back
   * to the OAuth provider. Used instead of `handleAccessDenied` because the
   * user is not actually unauthorized — their token just expired/was revoked.
   *
   * EB-4580: also the runtime re-auth path (see `handleSessionExpired`), so it
   * keeps the query string in the return URL and no longer clears localStorage:
   * it holds UI preferences, the per-user search recents and the implicit-flow
   * retry counter — nothing token-related. The OAuth tokens live in
   * sessionStorage and are wiped below. `logout()` still clears both stores.
   */
  private reauthenticate(): void {
    const returnUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    sessionStorage.clear();
    this.oauthService.logOut(true);
    this.userDetails$.next(null);
    this.login(returnUrl);
  }

  /**
   * The implicit flow has no token refresh, and token validity was only checked
   * at bootstrap and on route activation (`authGuard`). An expired token in a
   * long-open tab therefore kept being sent, every backend answered 401, and the
   * Ctrl+K search (no navigation → no guard) rendered that as "no results".
   * Called by `authInterceptor` (token expired before sending) and on tab
   * re-focus; bounces through the OAuth provider back to the current route.
   * With a live SSO session at the auth server that is just a reload.
   *
   * Never acts:
   * - before the user is `authorized` — bootstrap keeps its handling in `configure()`;
   * - during `logout()` — it owns the session teardown;
   * - in a hidden tab — a background poll must not discard an unsaved form the
   *   operator left open; the re-focus check re-authenticates on return;
   * - within the cooldown after the previous re-auth (stored AFTER
   *   `reauthenticate()` clears sessionStorage, so it survives the redirect).
   *
   * @returns true when a re-auth redirect is in progress (callers should not
   * fire further requests), false when it was not started.
   * @ticket EB-4580
   */
  handleSessionExpired(trigger: SessionExpiredTrigger): boolean {
    if (this.sessionExpiredRedirecting) return true;
    if (this._accessState() !== 'authorized' || this.loggingOut) return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    const sinceLastReauth = Date.now() - Number(sessionStorage.getItem(AuthService.SESSION_EXPIRED_REAUTH_AT_KEY) ?? '0');
    // A negative value means the clock moved backwards — treat as cooled down.
    if (sinceLastReauth >= 0 && sinceLastReauth < AuthService.SESSION_EXPIRED_REAUTH_COOLDOWN_MS) {
      this.logAuthEvent('session-expired-reauth-suppressed', { trigger });
      return false;
    }
    this.sessionExpiredRedirecting = true;
    // Best effort: the log bridge sends the same expired Bearer and the redirect
    // follows immediately, so this entry may never arrive — buffering is EB-4587.
    this.logAuthEvent('session-expired', { trigger });
    this.reauthenticate();
    sessionStorage.setItem(AuthService.SESSION_EXPIRED_REAUTH_AT_KEY, String(Date.now()));
    return true;
  }

  /**
   * A backend answered 401. A locally expired token goes straight to re-auth.
   * A token that is still valid locally may be revoked, or inside the library's
   * 10-minute clock-skew window — or the backend uses 401 for something else.
   * Ask the auth server (`check_token`) first and only re-authenticate on a
   * definitive rejection, so a misbehaving endpoint can't bounce the operator
   * through the login on every call. Parallel 401s share one check, and a
   * `valid` verdict is reused for `TOKEN_VALID_VERDICT_TTL_MS`, so an endpoint that
   * keeps answering 401 costs one check and one log entry per minute, not per request.
   * An unreachable auth server never triggers a re-auth.
   * @ticket EB-4580
   */
  handleUnauthorized(): void {
    if (this.sessionExpiredRedirecting || this.unauthorizedCheckInFlight) return;
    if (this._accessState() !== 'authorized' || this.loggingOut) return;
    if (!this.oauthService.hasValidAccessToken()) {
      this.handleSessionExpired('http-401');
      return;
    }
    if (Date.now() - this.tokenValidVerdictAt < AuthService.TOKEN_VALID_VERDICT_TTL_MS) return;
    this.unauthorizedCheckInFlight = true;
    void this.checkToken().then((result) => {
      this.unauthorizedCheckInFlight = false;
      if (result === 'valid') {
        this.tokenValidVerdictAt = Date.now();
        this.logAuthEvent('http-401-token-still-valid', {});
      } else if (result === 'unavailable') {
        this.logAuthEvent('http-401-check-token-unavailable', {});
      } else {
        this.handleSessionExpired('http-401');
      }
    });
  }

  /**
   * Re-check the token when the operator returns to the tab (tab switch or
   * window focus), so an overnight-open AMIE re-authenticates BEFORE the first
   * request fails — and before the operator starts typing into a form.
   * @ticket EB-4580
   */
  private watchTokenExpiryOnReturn(): void {
    if (typeof window === 'undefined' || this.tokenExpiryWatchStarted) return;
    this.tokenExpiryWatchStarted = true;
    const check = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (this.oauthService.getAccessToken() && !this.oauthService.hasValidAccessToken()) {
        this.handleSessionExpired('tab-visible');
      }
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    });
  }

  /** Synchronous read of current userId (0 if not loaded yet) */
  get currentUserId(): number {
    return this.userDetails$.value?.userId ?? 0;
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  get accessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  login(returnUrl?: string): void {
    // Stash returnUrl in sessionStorage (survives same-tab OAuth redirect)
    // so configure() can restore the path after the redirect lands at the
    // bare `redirectUri` (origin).
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      sessionStorage.setItem(AuthService.RETURN_URL_KEY, returnUrl);
    }
    this.oauthService.initImplicitFlow();
  }

  logout(options: { redirectToLogin?: boolean } = {}): void {
    // EB-3775: send GET /ssologout WITH the Bearer header still attached so the
    // OAuth server knows whose session to terminate (matches the working flow
    // in the legacy CMT — `authenticationService.js` `ssoLogout`/`finalLogout`).
    // Only AFTER the server response do we wipe local state. If we cleared the
    // token first, the request would go out without Authorization, the server
    // can't identify the session, and the SSO cookie stays alive → silent
    // re-auth on the next `/oauth/authorize` hit (= "can't log out").
    //
    // CR-1 (token-live-race) trade-off: the access token is briefly still
    // present in storage during the ~50-300 ms /ssologout round-trip. Once the
    // server responds, that token is invalidated server-side anyway. Accepted.
    //
    //   • redirectToLogin: true (default — user-initiated "Sign out") →
    //     after /ssologout completes, full page reload to origin so the fresh
    //     app bootstraps with no token, hits `authGuard` → `login()` →
    //     `initImplicitFlow()` → OAuth /authorize sees no session (we just
    //     killed it) → user sees the login form.
    //   • redirectToLogin: false (access-denied path from
    //     `handleAccessDenied`) → after /ssologout completes, stay on the
    //     Access-Denied screen (AC5 — no redirect loop back to OAuth login).
    const redirectToLogin = options.redirectToLogin ?? true;
    this.loggingOut = true;
    // EB-3798: emit before the ssologout round-trip. Fires for both
    // user-initiated sign-out and the access-denied internal logout — both are
    // genuine session terminations.
    this.loggingService.info('auth.logout');
    const logoutUrl = `${environment.authUrl}/ssologout`;

    const finalize = (): void => {
      this.oauthService.logOut(true);
      sessionStorage.clear();
      localStorage.clear();
      this.userDetails$.next(null);
      this._ownAccount.set(null);
      this.ownAccountLoading.set(false);
      // H2: cancel any in-flight ensureOwnAccountLoaded() chain so its
      // pending response can't overwrite the just-cleared _ownAccount.
      this.logoutTrigger$.next();
      if (redirectToLogin && typeof window !== 'undefined') {
        window.location.href = window.location.origin;
      }
    };

    this.http.get(logoutUrl, { responseType: 'text' }).subscribe({
      next: () => finalize(),
      error: () => finalize(),
    });
  }

  fetchUserDetails(): Observable<UserDetails> {
    return this.http
      .get<UserDetails>(`${environment.authUrl}/userdetails`)
      .pipe(tap((user) => this.userDetails$.next(user)));
  }

  /** Load user details once — idempotent, safe to call multiple times */
  ensureUserDetailsLoaded(): void {
    if (this.userDetails$.value !== null) return;
    this.fetchUserDetails().subscribe();
  }

  /**
   * EB-3801: Load the logged-in employee's own customer/UA/PS once and expose
   * via the `ownAccount` signal. Used by the titlebar user-menu "My account"
   * quick-jump rows. Idempotent — first call kicks off the fetch chain
   * (check_token → useraccounts/{id}), subsequent calls are no-ops.
   *
   * Failures are swallowed (signal stays null) — the menu just hides the
   * section rather than blocking the user.
   */
  ensureOwnAccountLoaded(): void {
    if (this._ownAccount() !== null || this.ownAccountLoading()) return;
    const token = this.oauthService.getAccessToken();
    if (!token) return;
    this.ownAccountLoading.set(true);
    // Code-Review I1: see verifyTokenWithServer — token in form body, not query.
    const url = `${environment.authUrl}/oauth/check_token`;
    const body = new URLSearchParams({ token }).toString();
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' });
    // H2: switchMap chain (no nested subscribe) piped through takeUntil(
    // logoutTrigger$) so a mid-flight logout cancels the chain before any
    // late response can race-write _ownAccount.
    this.http
      .post<{ details?: { userAccountId?: number; brandId?: number; phoneServiceData?: Array<{ phoneServiceId?: number }> } }>(
        url,
        body,
        { headers },
      )
      .pipe(
        switchMap((body) => {
          const d = body?.details;
          const uaId = d?.userAccountId;
          if (!uaId) return EMPTY;
          const psId = d?.phoneServiceData?.[0]?.phoneServiceId ?? null;
          const brandId = d?.brandId ?? null;
          return this.http
            .get<{ customerId: number }>(`${environment.userAccountUrl}/useraccounts/${uaId}`)
            .pipe(map((ua) => ({ customerId: ua.customerId, userAccountId: uaId, phoneServiceId: psId, brandId })));
        }),
        takeUntil(this.logoutTrigger$),
      )
      .subscribe({
        next: (own) => {
          // H2: defensive re-check — if logout cleared the token while the
          // request was in flight (and takeUntil hasn't fired yet), abort.
          if (!this.oauthService.getAccessToken()) {
            this.ownAccountLoading.set(false);
            return;
          }
          this._ownAccount.set(own);
          this.ownAccountLoading.set(false);
        },
        error: (err) => {
          this.ownAccountLoading.set(false);
          this.loggingService.warn('auth.own_account_load_failed', {
            full_message: safeHttpMessage(err),
          });
        },
        complete: () => {
          // EMPTY short-circuit (no userAccountId) or takeUntil cancellation:
          // always clear the loading flag so subsequent calls can retry.
          this.ownAccountLoading.set(false);
        },
      });
  }

  hasRole(role: string): boolean {
    const user = this.userDetails$.value;
    return user?.roles?.includes(role) ?? false;
  }

  // ---------------------------------------------------------------------------
  // EB-3775 — Role gate
  // ---------------------------------------------------------------------------

  private async evaluateAccess(): Promise<void> {
    try {
      // AUTH_TIMEOUT_MS protects APP_INITIALIZER from a stalled /userdetails —
      // falls through to handleAccessDenied('userdetails-error') → user sees
      // the service-error variant with Retry (AD-9).
      const details = await firstValueFrom(this.fetchUserDetails().pipe(timeout(AuthService.AUTH_TIMEOUT_MS)));
      if (details?.roles?.includes(REQUIRED_ROLE)) {
        // EB-3709 I1: load employee modules in a dedicated try/catch so a
        // permissions-endpoint failure is logged with a distinct reason
        // ('permissions-error') for ops triage, rather than masquerading as
        // 'userdetails-error' under the outer catch. UX is identical — same
        // 'denied-error' state, same Retry screen.
        try {
          await this.permissionService.loadModules(details.userId);
        } catch (err) {
          this.handleAccessDenied('permissions-error', details, err);
          return;
        }
        if (!this.permissionService.canAccessAmie()) {
          this.handleAccessDenied('no-role', details);
          return;
        }
        // EB-3798: single unambiguous login-success point — valid token,
        // user details loaded, role + AMIE module checks all passed. The
        // logged-in userId is auto-attached by LoggingService as `_userId`
        // (D8 drops the bare `employee` field), so no identity field is set
        // here explicitly.
        this._accessState.set('authorized');
        this.loggingService.info('auth.login_success');
        this.watchTokenExpiryOnReturn();
      } else {
        this.handleAccessDenied('no-role', details ?? null);
      }
    } catch (err) {
      this.handleAccessDenied('userdetails-error', null, err);
    }
  }

  private handleAccessDenied(
    reason: 'no-role' | 'userdetails-error' | 'permissions-error',
    details: UserDetails | null,
    err?: unknown,
  ): void {
    this.logAccessDenied(reason, details, err);
    // Set state FIRST so the @switch in app.html flips to the access-denied
    // view immediately. Then call logout() with redirectToLogin=false to
    // clear the local session without sending the user back to the OAuth
    // login form (which would re-enter the auth flow and create the redirect
    // loop AC5 forbids).
    this._accessState.set(reason === 'no-role' ? 'denied' : 'denied-error');
    // Scrub the deep-linked URL from the address bar / history so customer/UA/PS
    // IDs from the requested path don't leak via browser history or bookmarks
    // (CR-6). Uses replaceState — no router dependency, no extra route needed.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', '/');
    }
    this.logout({ redirectToLogin: false });
  }

  private logAccessDenied(
    reason: 'no-role' | 'userdetails-error' | 'permissions-error',
    details: UserDetails | null,
    err?: unknown,
  ): void {
    const payload = {
      event: 'access_denied',
      reason,
      // Redacted metadata only — full identifiers are joined server-side in
      // Graylog. Browser extensions, Sentry, screen recordings should NOT see
      // raw login/userId/roles.
      userIdPresent: details?.userId != null,
      loginPresent: details?.login != null,
      rolesCount: details?.roles?.length ?? 0,
      requiredRole: REQUIRED_ROLE,
      // safeHttpMessage: an HttpErrorResponse (userdetails-error path) → status +
      // path (no query leak); String() would yield a useless "[object Object]".
      ...(err !== undefined ? { error: safeHttpMessage(err) } : {}),
    };
    this.loggingService.warn('auth.access_denied', payload);
  }

  /**
   * Code-Review M2: Single sink for the new EB-3781 auth events, emitted via
   * LoggingService (`auth.event`) so they land in Graylog in prod and mirror to
   * the console in dev. Kept separate from `logAccessDenied` so the EB-3775
   * `access_denied` event keeps its stable shape for downstream filters.
   *
   * @ticket EB-3798
   */
  private logAuthEvent(
    event:
      | 'check-token-rejected'
      | 'fragment-stall-retry'
      | 'fragment-stall-exhausted'
      | 'oauth-error-fragment'
      | 'implicit-flow-rejected'
      | 'session-expired'
      | 'session-expired-reauth-suppressed'
      | 'http-401-token-still-valid'
      | 'http-401-check-token-unavailable',
    extra: Record<string, unknown>,
  ): void {
    const payload = {
      event,
      ...extra,
    };
    this.loggingService.warn('auth.event', payload);
  }
}
